import "./tabsHomePage.css";
import React, {
  useState,
  useEffect,
  useContext,
  Suspense,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { TabContext, TabList } from "@mui/lab";
import { Tab, Grid2 as Grid, IconButton, Tooltip } from "@mui/material";
import { Close } from "@mui/icons-material";
import ScrollToTop from "react-scroll-to-top";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  NavigateContext,
  M_StateActions,
  MLateralMenu,
  LoadingBar,
  MButton,
} from "src/M_Components";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PropTypes from "prop-types";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import { STORAGE_CARRIER, STORAGE_USER } from "src/components/auth/storageKeys";
import { useCarrierAuth } from "src/components/auth/CarrierAuthProvider";
import { useUserAuth } from "src/components/auth/UserAuthProvider";

// Pages
import ChangeLogPage from "src/pages/Logs/LogPage";
import SettingsPage from "../settings/SettingsPage";
import UserConfigCrud from "src/pages/Users/userConfigCrud";
import RoleConfigCrud from "src/pages/Roles/roleConfigCrud";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { hasPermission } from "src/components/utils/permissionsHelper";
import { ModuleActionEnumReverse } from "src/M_Components/M_Utils/permissions";
import AccountPage from "src/pages/Accounts/accountConfig";
import AccountConfigCrud from "src/pages/Accounts/AccountConfigCrud";
import ContactPage from "src/pages/Contacts/ContactPage";
import ContactConfigCrud from "src/pages/Contacts/ContactConfigCrud";
import ShipperConfigPage from "src/pages/Shippers/ShipperConfigPage";
import ShipperMarketManagementPage from "src/pages/Shippers/ShipperMarketManagementPage";
import ShipperCarrierManagementPage from "src/pages/Shippers/shipperCarrierManagementPage";
import ShipperConfigCrud from "src/pages/Shippers/shipperConfigCrud";
import CarrierConfigPage from "src/pages/Carriers/carrierConfigPage";
import CarrierLoginsConfigPage from "src/pages/Carriers/CarrierLoginsConfigPage";
import CarrierMarketManagementPage from "src/pages/Carriers/CarrierMarketManagementPage";
import CarrierTypeOfServiceManagementPage from "src/pages/Carriers/CarrierTypeOfServiceManagementPage";
import CarrierLoginsConfigCrud from "src/pages/Carriers/CarrierLoginsConfigCrud";
import CarrierConfigCrud from "src/pages/Carriers/carrierConfigCrud";
import MarketPage from "src/pages/Markets/marketPage";
import MarketConfigCrud from "src/pages/Markets/marketConfigCrud";
import LocationPage from "src/pages/Locations/locationPage";
import LocationConfigCrud from "src/pages/Locations/locationConfigCrud";
import CountryPage from "src/pages/Countries/countryPage";
import CountryConfigCrud from "src/pages/Countries/countryConfigCrud";
import ContinentConfigCrud from "src/pages/Countries/continentConfigCrud";
import CurrencyPage from "src/pages/Currencies/currencyPage";
import CurrencyConfigCrud from "src/pages/Currencies/currencyConfigCrud";
import TransportTypePage from "src/pages/TransportTypes/transportTypePage";
import TransportTypeConfigCrud from "src/pages/TransportTypes/transportTypeConfigCrud";
import LanePage from "src/pages/Lanes/lanePage";
import LaneConfigCrud from "src/pages/Lanes/laneConfigCrud";
import RoutePage from "src/pages/Routes/routePage";
import RouteConfigCrud from "src/pages/Routes/routeConfigCrud";
import RouteLaneConfigCrud from "src/pages/Routes/routeLaneConfigCrud";
import InstantQuotesSearchPage from "src/pages/InstantRequestForQuotes/InstantQuotesPage";
import InstantQuotesSearchConfigCrud from "src/pages/InstantRequestForQuotes/InstantQuotesSearchConfigCrud";
import InstantQuotesConfigCrud from "src/pages/InstantRequestForQuotes/InstantQuotesConfigCrud";
import SpotTenderPage from "src/pages/SpotTenders/SpotTenderPage";
import NonSpotTenderPage from "src/pages/NonSpotTenders/NonSpotTenderPage";
import CreateTenderPage from "src/pages/CreateTenders/CreateTenderPage";
import TenderOpenPage from "src/pages/Tenders/TenderOpenPage";
import TenderFinishedPage from "src/pages/Tenders/TenderFinishedPage";
import TenderPhaseConfig from "src/pages/NonSpotTenders/TenderPhaseConfig";
import AddressPage from "src/pages/Addresses/AddressPage";
import AddressConfigCrud from "src/pages/Addresses/AddressConfigCrud";
import ApiKeyConfigCrud from "src/pages/ApiKeys/ApiKeyConfigCrud";
import TenderConfigCrud from "src/pages/Tenders/TenderConfigCrud";
import TrackingPage from "src/pages/Tracking/TrackingPage";
import TrackingConfigCrud from "src/pages/Tracking/TrackingConfigCrud";

// Carrier invite / bid pages
import SpotTenderCarrierInvitePage from "src/pages/CarrierInvitation/SpotTender/SpotTenderCarrierInvitePage";
import NonSpotTenderCarrierInvitePage from "src/pages/CarrierInvitation/NonSpotTender/NonSpotTenderCarrierInvitePage";
import CarrierSubmitPage from "src/pages/CarrierInvitation/CarrierSubmitPage";
import CarrierInviteViewBidPage from "src/pages/CarrierInvitation/CarrierInviteViewBidPage";

//region Constants

/**
 * LocalStorage key used to preserve the pending carrier invite flow.
 *
 * This payload is created before the carrier logs in and is reused afterwards
 * so the application can resume the invitation flow automatically.
 */
const PENDING_INVITE_KEY = "carrier_invite_pending";

//endregion

//region Utility helpers

/**
 * Compares two values through JSON serialization.
 *
 * Purpose:
 * - Prevents unnecessary state writes when the semantic payload has not changed.
 * - Keeps menu and tab updates more stable in large layout components.
 *
 * @param {any} a - First value to compare.
 * @param {any} b - Second value to compare.
 * @returns {boolean} True when both values serialize to the same JSON representation.
 */
const jsonEqual = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

/**
 * Writes state only when the next value is materially different from the current value.
 *
 * Purpose:
 * - Reduces redundant renders in state branches that receive repeated equivalent payloads.
 *
 * @param {Function} setter - React state setter.
 * @param {any} nextVal - Next candidate value.
 */
const setIfChanged = (setter, nextVal) =>
  setter((prev) => (jsonEqual(prev, nextVal) ? prev : nextVal));

/**
 * Parses JSON safely.
 *
 * @param {string|null} raw - Raw JSON string.
 * @returns {any|null} Parsed value or null when parsing fails.
 */
function parseJsonSafe(raw) {
  if (!raw || typeof raw !== "string") {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Normalizes any incoming menu payload into a safe array.
 *
 * Accepted shapes:
 * - Array directly returned by the API
 * - Wrapped payloads such as { Items: [...] } or { data: [...] }
 * - Invalid or non-menu payloads fallback to an empty array
 *
 * Purpose:
 * - Prevents render-time failures such as "menus.map is not a function".
 *
 * @param {any} value - Raw menu payload.
 * @returns {Array} Safe menu array.
 */
function toMenuArray(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.Items)) {
    return value.Items;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  return [];
}

/**
 * Deep clones a menu tree after normalizing its source.
 *
 * Purpose:
 * - Avoids mutating shared menu references across re-renders.
 * - Ensures downstream tree transformations always operate on a safe array.
 *
 * @param {any} arr - Raw menu payload.
 * @returns {Array} Deep-cloned safe menu array.
 */
function clone(arr) {
  try {
    return JSON.parse(JSON.stringify(toMenuArray(arr)));
  } catch {
    return [];
  }
}

/**
 * Extracts the most reliable HTTP status code from normalized response shapes.
 *
 * Supported sources:
 * - BufferService metadata (__status)
 * - Standard payload fields (status, statusCode, StatusCode)
 * - Axios-like error.response.status
 *
 * Purpose:
 * - Allows 401 handling to work even when the caller receives a normalized
 *   payload instead of a raw Axios error instance.
 *
 * @param {any} value - Raw payload or error object.
 * @returns {number|null} Resolved HTTP status code or null when unavailable.
 */
function getPayloadStatus(value) {
  const status =
    value?.__status ??
    value?.status ??
    value?.statusCode ??
    value?.StatusCode ??
    value?.response?.status;

  return typeof status === "number" ? status : null;
}

/**
 * Extracts comparable authenticated carrier/account identity fields from the
 * carrier profile stored in localStorage.
 *
 * Supported fields:
 * - account.Id / account.AccountId / account.Code / account.AccountCode
 * - carrier.Id / carrier.CarrierId / carrier.Code / carrier.CarrierCode
 *
 * @param {any} profile - Parsed carrier profile.
 * @returns {object} Normalized identity object.
 */
function getAuthenticatedCarrierIdentity(profile) {
  return {
    accountId: profile?.account?.Id ?? profile?.account?.AccountId ?? null,
    accountCode: profile?.account?.Code ?? profile?.account?.AccountCode ?? null,
    carrierId: profile?.carrier?.Id ?? profile?.carrier?.CarrierId ?? null,
    carrierCode: profile?.carrier?.Code ?? profile?.carrier?.CarrierCode ?? null,
  };
}

/**
 * Extracts comparable identity fields from the pending invite payload stored in localStorage.
 *
 * Supported fields:
 * - accountId / account.Id / account.AccountId / accountCode
 * - carrierId / carrier.Id / carrier.CarrierId / carrierCode
 *
 * @param {any} invite - Parsed pending invite payload.
 * @returns {object} Normalized identity object.
 */
function getPendingInviteIdentity(invite) {
  return {
    accountId:
      invite?.accountId ??
      invite?.account?.Id ??
      invite?.account?.AccountId ??
      null,
    accountCode:
      invite?.accountCode ??
      invite?.account?.Code ??
      invite?.account?.AccountCode ??
      null,
    carrierId:
      invite?.carrierId ??
      invite?.carrier?.Id ??
      invite?.carrier?.CarrierId ??
      null,
    carrierCode:
      invite?.carrierCode ??
      invite?.carrier?.Code ??
      invite?.carrier?.CarrierCode ??
      null,
  };
}

/**
 * Extracts comparable identity fields directly from the current URL query string.
 *
 * Supported params:
 * - accountId
 * - accountCode
 * - carrierId
 * - carrierCode
 *
 * Purpose:
 * - Supports older or alternative invite flows that encode identity in the URL
 *   instead of persisting it into localStorage.
 *
 * @param {string} search - React Router location.search value.
 * @returns {object} Normalized identity object.
 */
function getInviteIdentityFromSearch(search) {
  const params = new URLSearchParams(search || "");

  return {
    accountId: params.get("accountId"),
    accountCode: params.get("accountCode"),
    carrierId: params.get("carrierId"),
    carrierCode: params.get("carrierCode"),
  };
}

/**
 * Merges two identity payloads, preferring the primary value when present.
 *
 * Purpose:
 * - Allows invite identity to come from localStorage, URL query, or both.
 *
 * @param {object} primary - Primary identity source.
 * @param {object} fallback - Fallback identity source.
 * @returns {object} Merged identity payload.
 */
function mergeCarrierIdentities(primary, fallback) {
  return {
    accountId: primary?.accountId ?? fallback?.accountId ?? null,
    accountCode: primary?.accountCode ?? fallback?.accountCode ?? null,
    carrierId: primary?.carrierId ?? fallback?.carrierId ?? null,
    carrierCode: primary?.carrierCode ?? fallback?.carrierCode ?? null,
  };
}

/**
 * Determines whether an identity payload contains at least one comparable field.
 *
 * @param {object} identity - Identity object.
 * @returns {boolean} True when at least one relevant field exists.
 */
function hasComparableCarrierIdentity(identity) {
  return Boolean(
    identity?.accountId !== null ||
    identity?.accountCode ||
    identity?.carrierId !== null ||
    identity?.carrierCode
  );
}

/**
 * Compares invite identity with authenticated carrier identity.
 *
 * Comparison priority:
 * - carrierId
 * - carrierCode
 * - accountId
 * - accountCode
 *
 * Only fields that exist on both sides are compared.
 *
 * @param {object} inviteIdentity - Identity extracted from invite context.
 * @param {object} authIdentity - Identity extracted from authenticated profile.
 * @returns {boolean} True when the invite belongs to a different carrier/account.
 */
function isDifferentCarrierInvite(inviteIdentity, authIdentity) {
  if (
    inviteIdentity?.carrierId !== null &&
    inviteIdentity?.carrierId !== undefined &&
    authIdentity?.carrierId !== null &&
    authIdentity?.carrierId !== undefined
  ) {
    return String(inviteIdentity.carrierId) !== String(authIdentity.carrierId);
  }

  if (inviteIdentity?.carrierCode && authIdentity?.carrierCode) {
    return (
      String(inviteIdentity.carrierCode).trim().toLowerCase() !==
      String(authIdentity.carrierCode).trim().toLowerCase()
    );
  }

  if (
    inviteIdentity?.accountId !== null &&
    inviteIdentity?.accountId !== undefined &&
    authIdentity?.accountId !== null &&
    authIdentity?.accountId !== undefined
  ) {
    return String(inviteIdentity.accountId) !== String(authIdentity.accountId);
  }

  if (inviteIdentity?.accountCode && authIdentity?.accountCode) {
    return (
      String(inviteIdentity.accountCode).trim().toLowerCase() !==
      String(authIdentity.accountCode).trim().toLowerCase()
    );
  }

  return false;
}

/**
 * Prefixes internal MenuLabelUrl paths with "/carrier/home" for the carrier realm
 * unless they already point to the carrier namespace or to an external URL.
 *
 * Important:
 * - HashRouter injects "#" automatically.
 * - MenuLabelUrl should therefore never contain "#".
 *
 * Examples:
 * - "/SpotTenderQuotes" -> "/carrier/home/SpotTenderQuotes"
 * - "https://external"  -> unchanged
 * - "/carrier/home/xyz" -> unchanged
 *
 * @param {Array} menus - Raw or normalized menu array.
 * @returns {Array} Remapped carrier menu tree.
 */
function remapCarrierMenuUrls(menus) {
  const PREFIX = "/carrier/home";
  const src = clone(menus);

  const patchUrl = (u) => {
    if (!u || typeof u !== "string") return u;

    if (u.startsWith("/carrier")) return u;

    if (
      /^[a-zA-Z]+:\/\//.test(u) ||
      u.startsWith("mailto:") ||
      u.startsWith("tel:")
    ) {
      return u;
    }

    if (u.startsWith("/")) return `${PREFIX}${u}`;

    return u;
  };

  const walk = (items) => {
    (items || []).forEach((m) => {
      m.MenuLabelUrl = patchUrl(m.MenuLabelUrl);

      if (Array.isArray(m.SubMenus)) {
        walk(m.SubMenus);
      }
    });
  };

  walk(src);
  return src;
}

//endregion

//region Tab label helper

/**
 * Renders a tab label that supports:
 * - fixed visual width,
 * - ellipsis when the label fits,
 * - marquee-style scrolling when the label overflows.
 *
 * Purpose:
 * - Keeps the tab bar stable while still allowing long translated labels to remain readable.
 *
 * @param {object} props - Component props.
 * @param {string} props.text - Visible tab label.
 * @returns {JSX.Element} Tab label element.
 */
function TabLabel({ text }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const label = textRef.current;

    if (!container || !label) return undefined;

    /**
     * Re-evaluates whether the rendered label exceeds the visible tab width.
     */
    const checkOverflow = () => {
      const overflowing = label.scrollWidth > container.clientWidth + 1;
      setIsOverflowing(overflowing);
    };

    checkOverflow();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(checkOverflow);
      ro.observe(container);
      ro.observe(label);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [text]);

  return (
    <span
      ref={containerRef}
      className={`tabLabelContainer ${isOverflowing ? "tabLabel--scroll" : ""}`}
    >
      <span ref={textRef} className="tabLabelText">
        {text}
      </span>
    </span>
  );
}

TabLabel.propTypes = {
  text: PropTypes.string,
};

//endregion

//region Main component

/**
 * Provides the main realm-aware home shell used by the application.
 *
 * Responsibilities:
 * - resolves user or carrier realm from the current route,
 * - restores persisted tabs and menu state,
 * - loads and normalizes menu trees safely,
 * - handles menu navigation and dynamic tabs,
 * - routes all feature pages inside the common shell,
 * - redirects to login when a protected bootstrap request becomes unauthorized,
 * - forces a fresh carrier login when an invite belongs to a different carrier
 *   than the currently authenticated carrier session.
 *
 * @param {object} props - Component props.
 * @param {Array} props.roles - Current role/permission claims used to filter visible menus.
 * @returns {JSX.Element} Rendered home shell.
 */
export default function TabsHomePage({ roles }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const ctx = useContext(LoadingContext);

  const { logout: carrierLogout } = useCarrierAuth();
  const { logout: userLogout } = useUserAuth();

  const dispatch = useDispatch();
  const M_StateData = useSelector((state) => state.StateKeyValue);

  //region State and refs

  const [showMenu, setShowMenu] = useState(false);
  const [menus, _setMenus] = useState([]);

  const [defaultAccountName, setDefaultAccountName] = useState("");
  const [defaultShipperName, setDefaultShipperName] = useState("");
  const [carrierAccountName, setCarrierAccountName] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [cargofiveApiKeyId, setCargofiveApiKeyId] = useState("");

  const [selectedTab, setSelectedTab] = useState("menu");
  const [lastOpenTab, setLastOpenTab] = useState("");
  const [tabs, setTabs] = useState([]);
  const [tabIndex, setTabIndex] = useState(1);

  const [showButtons, setShowButtons] = useState(true);
  const [isFirstTime, setIsFirstTime] = useState(true);

  /**
   * Guards used to keep menu bootstrap deterministic.
   *
   * Purpose:
   * - prevents repeated fetch attempts caused by context re-renders,
   * - prevents repeated unauthorized handling,
   * - prevents duplicate error toasts for the same bootstrap failure.
   */
  const menusBootstrapStartedRef = useRef(false);
  const unauthorizedHandledRef = useRef(false);
  const menuErrorToastShownRef = useRef(false);

  /**
   * Guard used to avoid repeated logout loops when the same mismatched invite
   * is evaluated multiple times during the same navigation cycle.
   */
  const inviteMismatchHandledRef = useRef(false);

  /**
   * Stores the latest context callbacks that are consumed by effects.
   *
   * Purpose:
   * - keeps side-effect callbacks fresh,
   * - avoids making bootstrap effects depend on unstable context function identities.
   */
  const toastMessagesRef = useRef(ctx.toastMessages);
  const setIsLoadRef = useRef(ctx.setIsLoad);

  useEffect(() => {
    toastMessagesRef.current = ctx.toastMessages;
  }, [ctx.toastMessages]);

  useEffect(() => {
    setIsLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  //endregion

  //region Derived values and memoized helpers

  const IS_CARRIER = pathname.startsWith("/carrier");
  const BASE = IS_CARRIER ? "/carrier/home" : "/home";
  const defaultUrl = ctx.DEFAULTURL;

  const bufferServiceMenu = useMemo(
    () => new BufferService("Menus/All", defaultUrl),
    [defaultUrl]
  );

  /**
   * Safe menu setter.
   *
   * Purpose:
   * - guarantees that menu state always remains an array,
   * - prevents invalid localStorage or API payloads from reaching render code.
   *
   * @param {any} val - Raw menu payload.
   */
  const setMenus = useCallback((val) => {
    setIfChanged(_setMenus, toMenuArray(val));
  }, []);

  /**
   * Evaluates whether the current role collection allows a specific module/action pair.
   *
   * @param {string} moduleName - Target module name.
   * @param {string|null} actionName - Target action name.
   * @returns {boolean} True when the permission is granted.
   */
  const checkPermissions = useCallback(
    (moduleName, actionName) => hasPermission(roles, moduleName, actionName),
    [roles]
  );

  /**
   * Resolves a translated label while falling back to the original key when the
   * translation is missing or invalid.
   *
   * @param {string} key - Translation key.
   * @returns {string} Visible label.
   */
  const translateLabel = useCallback(
    (key) => {
      const translated = i18n.t(key);
      const isValid =
        translated &&
        typeof translated === "string" &&
        translated.trim().length > 0 &&
        translated !== key;

      return isValid ? translated.trim() : key;
    },
    [i18n]
  );

  /**
   * List of valid tab values accepted by the visible TabContext.
   *
   * Purpose:
   * - prevents Material UI tabs from receiving stale or invalid selected values.
   */
  const validTabValues = useMemo(
    () => ["menu", ...tabs.map((tab) => String(tab.value))],
    [tabs]
  );

  /**
   * Selected tab value exposed to TabContext after validation.
   *
   * Purpose:
   * - keeps the tab system stable while tabs are being created, closed, or restored.
   */
  const uiTabValue = useMemo(() => {
    const current = String(selectedTab);

    if (validTabValues.includes(current)) {
      return current;
    }

    if (validTabValues.length > 0) {
      return String(validTabValues[0]);
    }

    return "menu";
  }, [selectedTab, validTabValues]);

  //endregion

  //region Logout and routing helpers

  /**
   * Clears the active realm session and redirects to the appropriate login page.
   *
   * Behavior:
   * - preserves the current language preference,
   * - clears the current realm auth state through its provider,
   * - resets local component state related to shell navigation,
   * - clears persisted Redux UI state,
   * - redirects to the corresponding login entry.
   */
  const handleLogout = useCallback(() => {
    setIsLoadRef.current?.(true);

    const currentLng = localStorage.getItem("lng");

    if (IS_CARRIER && typeof carrierLogout === "function") {
      try {
        carrierLogout({ clearStorage: true });
      } catch {
        // Ignore logout provider errors and continue with navigation cleanup.
      }
    } else if (!IS_CARRIER && typeof userLogout === "function") {
      try {
        userLogout({ clearStorage: true });
      } catch {
        // Ignore logout provider errors and continue with navigation cleanup.
      }
    }

    setShowMenu(false);
    setMenus([]);
    setTabs([]);
    setSelectedTab("menu");
    setLastOpenTab("");
    setTabIndex(1);

    menusBootstrapStartedRef.current = false;
    unauthorizedHandledRef.current = false;
    menuErrorToastShownRef.current = false;

    if (currentLng) {
      localStorage.setItem("lng", currentLng);
    }

    dispatch(M_StateActions.DeleteAll_M_State());

    navigate(IS_CARRIER ? "/carrier/login" : "/login", {
      replace: true,
    });

    setTimeout(() => {
      setIsLoadRef.current?.(false);
    }, 250);
  }, [IS_CARRIER, carrierLogout, userLogout, dispatch, navigate, setMenus]);

  /**
   * Normalizes any route into the current realm base.
   *
   * Rules:
   * - empty values resolve to BASE,
   * - already-prefixed values remain unchanged,
   * - absolute application routes are re-based when required,
   * - bare segments are appended after BASE.
   *
   * @param {string} url - Raw route input.
   * @returns {string} Normalized realm-aware route.
   */
  const toBase = useCallback(
    (url) => {
      let result;

      if (!url || url === "/" || url === BASE) {
        result = BASE;
      } else if (url.startsWith(BASE)) {
        result = url;
      } else if (url.startsWith("/carrier/home") || url.startsWith("/home")) {
        result = url;
      } else if (url.startsWith("/")) {
        result = `${BASE}${url}`;
      } else {
        result = `${BASE}/${url}`;
      }

      return result;
    },
    [BASE]
  );

  /**
   * Navigates back to the realm landing menu.
   *
   * Purpose:
   * - restores the shell to the landing state represented by pseudo-tab "menu".
   */
  const menusRoute = useCallback(() => {
    setShowButtons(true);
    setShowMenu(true);
    setSelectedTab("menu");
    navigate(BASE);
  }, [navigate, BASE]);

  /**
   * Navigates to a concrete tab route inside the current realm.
   *
   * Purpose:
   * - centralizes route transitions when a user selects an existing tab.
   *
   * @param {string} url - Tab route relative to the shell.
   */
  const tabsRoutes = useCallback(
    (url) => {
      setShowMenu(false);
      setShowButtons(false);
      setLastOpenTab(String(selectedTab));
      navigate(toBase(url));
    },
    [navigate, toBase, selectedTab]
  );

  /**
   * Opens a given route into a new dynamic tab and optionally navigates to it.
   *
   * Behavior:
   * - keeps the tab label translatable through i18nKey,
   * - supports middle-click creation without forced navigation,
   * - stores the created tab in the dynamic tabs collection.
   *
   * @param {string} url - Raw route, absolute or relative.
   * @param {string} passLabel - Optional tab label override.
   * @param {MouseEvent} event - Optional mouse event used to detect middle-click.
   */
  const goToPage = useCallback(
    (url, passLabel, event) => {
      const normalizedUrl =
        url.startsWith("/home") || url.startsWith("/carrier/home")
          ? url.replace(/^\/(carrier\/home|home)\//, "")
          : url.startsWith("/")
            ? url.substring(1)
            : url;

      const routeKey = normalizedUrl.split(/[?#/]/)[0];
      const translationKey = passLabel || routeKey;
      const label = translateLabel(translationKey);
      const nextValue = String(tabIndex);

      if (event?.button !== 1) {
        navigate(toBase(url));
        setShowButtons(false);
        setShowMenu(false);
        setSelectedTab(nextValue);
      }

      setTabs((prev) => {
        const newTab = {
          value: nextValue,
          label,
          tabUrl: normalizedUrl,
          i18nKey: translationKey,
        };

        return [...prev, newTab];
      });

      dispatch(
        M_StateActions.addM_State({
          key: "selectedTab",
          value: nextValue,
        })
      );

      setTabIndex((prev) => prev + 1);
      setLastOpenTab(String(selectedTab));

      toastMessagesRef.current?.(
        "success",
        "",
        translateLabel("MessageTabCreated")
      );
    },
    [dispatch, navigate, toBase, tabIndex, selectedTab, translateLabel]
  );

  /**
   * Handles clicks on the shell logo.
   *
   * Behavior:
   * - when the menu is closed, it behaves like "Home" and returns to the landing menu;
   * - when the menu is open, it only collapses the menu.
   */
  const handleLogoClick = useCallback(() => {
    if (!showMenu) {
      menusRoute();
    } else {
      setShowMenu(false);
    }
  }, [showMenu, menusRoute]);

  //endregion

  //region Boot and persistence effects

  /**
   * Resets one-shot bootstrap guards whenever the active realm changes.
   *
   * Purpose:
   * - allows a fresh bootstrap sequence when the shell switches
   *   between user and carrier routes.
   */
  useEffect(() => {
    menusBootstrapStartedRef.current = false;
    unauthorizedHandledRef.current = false;
    menuErrorToastShownRef.current = false;
  }, [IS_CARRIER]);

  /**
   * Resets the invite mismatch guard whenever the invite route context changes.
   *
   * Purpose:
   * - allows a fresh comparison when the user opens a different invite link.
   */
  useEffect(() => {
    inviteMismatchHandledRef.current = false;
  }, [IS_CARRIER, pathname, search]);

  /**
   * Restores the visible account badges according to the active realm.
   *
   * User realm:
   * - reads account and default shipper labels from legacy storage keys.
   *
   * Carrier realm:
   * - reads account and carrier labels from the carrier profile payload.
   */
  useEffect(() => {
    if (!IS_CARRIER) {
      const dAcc = localStorage.getItem("defaultAccountName");
      const dShip = localStorage.getItem("defaultShipperName");
      const cfid = localStorage.getItem("cargofiveApiKeyId");

      if (dAcc !== null) setDefaultAccountName(dAcc);
      if (dShip !== null) setDefaultShipperName(dShip);
      if (cfid !== null) setCargofiveApiKeyId(cfid);

      return;
    }

    try {
      const raw = localStorage.getItem(STORAGE_CARRIER?.profile);
      if (!raw) return;

      const parsed = JSON.parse(raw) || {};
      const accName = parsed?.account?.Name ?? "";
      const carName = parsed?.carrier?.Name ?? "";

      setCarrierAccountName(accName);
      setCarrierName(carName);
    } catch {
      // Ignore invalid profile payloads and keep empty labels.
    }
  }, [IS_CARRIER]);

  /**
   * Forces a fresh carrier login when a pending invite belongs to a different
   * carrier/account than the currently authenticated carrier session.
   *
   * Identity sources checked:
   * - Pending invite payload in localStorage
   * - Invite URL query string
   * - Authenticated carrier profile in localStorage
   *
   * Important:
   * - The pending invite context is preserved across the forced logout so the
   *   flow can resume after the correct carrier logs in.
   * - If the invite flow stores only a token and no identity metadata, the
   *   definitive mismatch check may still need to happen later inside
   *   CarrierInviteViewBidPage after resolving the invite token.
   */
  useEffect(() => {
    if (!IS_CARRIER) return;

    const isInviteWrapperRoute = pathname.startsWith(
      "/carrier/home/CarrierInviteViewBid"
    );
    if (!isInviteWrapperRoute) return;

    if (inviteMismatchHandledRef.current) return;

    const activeCarrierToken = localStorage.getItem(STORAGE_CARRIER?.token);
    if (!activeCarrierToken) return;

    const rawPendingInvite = localStorage.getItem(PENDING_INVITE_KEY);
    const rawCarrierProfile = localStorage.getItem(STORAGE_CARRIER?.profile);

    const parsedPendingInvite = parseJsonSafe(rawPendingInvite);
    const parsedCarrierProfile = parseJsonSafe(rawCarrierProfile);

    if (!parsedCarrierProfile) return;

    const inviteIdentityFromStorage = getPendingInviteIdentity(parsedPendingInvite);
    const inviteIdentityFromSearch = getInviteIdentityFromSearch(search);
    const inviteIdentity = mergeCarrierIdentities(
      inviteIdentityFromStorage,
      inviteIdentityFromSearch
    );
    const authIdentity = getAuthenticatedCarrierIdentity(parsedCarrierProfile);

    if (!hasComparableCarrierIdentity(inviteIdentity)) return;
    if (!hasComparableCarrierIdentity(authIdentity)) return;

    const mismatch = isDifferentCarrierInvite(inviteIdentity, authIdentity);
    if (!mismatch) return;

    inviteMismatchHandledRef.current = true;

    /**
     * Preserve the invite payload explicitly before logout.
     *
     * Purpose:
     * - Some logout implementations clear localStorage auth state aggressively.
     * - Rewriting the pending invite immediately after logout ensures the invite
     *   flow survives and can resume after the correct carrier logs in.
     */
    const preservedInvite = rawPendingInvite;

    handleLogout();

    if (preservedInvite) {
      try {
        localStorage.setItem(PENDING_INVITE_KEY, preservedInvite);
      } catch {
        // Ignore storage write failures and continue with forced login.
      }
    }
  }, [IS_CARRIER, pathname, search, handleLogout]);

  /**
   * Rehydrates menus from realm-specific localStorage before any fallback request runs.
   *
   * Rules:
   * - user realm    -> STORAGE_USER.menus
   * - carrier realm -> STORAGE_CARRIER.menus, then remap internal carrier URLs
   */
  useEffect(() => {
    const storageKey = IS_CARRIER ? STORAGE_CARRIER?.menus : STORAGE_USER?.menus;

    try {
      const rawMenus = localStorage.getItem(storageKey);

      if (!rawMenus) {
        setMenus([]);
        return;
      }

      const parsedMenus = JSON.parse(rawMenus);
      const normalizedMenus = toMenuArray(parsedMenus);

      if (IS_CARRIER) {
        setMenus(remapCarrierMenuUrls(normalizedMenus));
        return;
      }

      setMenus(normalizedMenus);
    } catch {
      setMenus([]);
    }
  }, [IS_CARRIER, setMenus]);

  /**
   * Performs a single fallback menu bootstrap request when the active realm
   * does not already have a usable menu tree in storage.
   *
   * Behavior:
   * - runs once per realm activation,
   * - normalizes successful payloads,
   * - stores the result under the correct realm key,
   * - redirects to login when the request becomes unauthorized.
   */
  useEffect(() => {
    if (menusBootstrapStartedRef.current) return;

    const storageKey = IS_CARRIER ? STORAGE_CARRIER?.menus : STORAGE_USER?.menus;

    let hasStoredMenus = false;

    try {
      const rawMenus = localStorage.getItem(storageKey);

      if (rawMenus) {
        const parsedMenus = JSON.parse(rawMenus);
        const normalizedMenus = toMenuArray(parsedMenus);
        hasStoredMenus = normalizedMenus.length > 0;
      }
    } catch {
      hasStoredMenus = false;
    }

    if (hasStoredMenus) {
      return;
    }

    menusBootstrapStartedRef.current = true;

    let cancelled = false;

    bufferServiceMenu
      .get()
      .then((res) => {
        if (cancelled) return;

        const failed = isFailurePayload(res) || res?.name === "AxiosError";

        if (failed) {
          const status = getPayloadStatus(res);

          if (status === 401 && !unauthorizedHandledRef.current) {
            unauthorizedHandledRef.current = true;
            handleLogout();
            return;
          }

          setMenus([]);

          if (!menuErrorToastShownRef.current) {
            menuErrorToastShownRef.current = true;
            toastMessagesRef.current?.(
              "error",
              "",
              apiMessage(res, translateLabel("ErrorLoadingData"))
            );
          }

          return;
        }

        const normalizedMenus = toMenuArray(res);
        const persistedMenus = IS_CARRIER
          ? remapCarrierMenuUrls(normalizedMenus)
          : normalizedMenus;

        try {
          localStorage.setItem(storageKey, JSON.stringify(persistedMenus));
        } catch {
          // Ignore storage write failures and keep the in-memory menu tree.
        }

        setMenus(persistedMenus);
      })
      .catch((err) => {
        if (cancelled) return;

        const status = getPayloadStatus(err);

        if (status === 401 && !unauthorizedHandledRef.current) {
          unauthorizedHandledRef.current = true;
          handleLogout();
          return;
        }

        setMenus([]);

        if (!menuErrorToastShownRef.current) {
          menuErrorToastShownRef.current = true;
          toastMessagesRef.current?.(
            "error",
            "",
            apiMessage(err, translateLabel("ErrorLoadingData"))
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [IS_CARRIER, bufferServiceMenu, handleLogout, setMenus, translateLabel]);

  /**
   * Restores persisted tabs and resolves the initial route displayed by the shell.
   *
   * Behavior:
   * - restores tabs, selection, visibility flags and indexes from Redux state,
   * - supports direct deep-links inside the shell,
   * - leaves the technical carrier invite wrapper without an automatic tab,
   * - executes only during the bootstrap phase.
   */
  useEffect(() => {
    if (!isFirstTime) return undefined;

    const restoredTabs = M_StateData.find((obj) => obj.key === "tabs")?.value;
    const restoredSelectedTab = M_StateData.find(
      (obj) => obj.key === "selectedTab"
    )?.value;
    const restoredShowButtons = M_StateData.find(
      (obj) => obj.key === "showButtons"
    )?.value;
    const restoredTabIndex = M_StateData.find((obj) => obj.key === "tabIndex")?.value;
    const restoredLastOpenTab = M_StateData.find(
      (obj) => obj.key === "lastOpenTab"
    )?.value;

    if (restoredTabs !== undefined) setIfChanged(setTabs, restoredTabs);
    if (restoredSelectedTab !== undefined) setSelectedTab(restoredSelectedTab);
    if (restoredShowButtons !== undefined) setShowButtons(restoredShowButtons);
    if (restoredTabIndex !== undefined) setTabIndex(restoredTabIndex);
    if (restoredLastOpenTab !== undefined) setLastOpenTab(restoredLastOpenTab);

    const timerId = setTimeout(() => {
      const alreadyOnChildRoute =
        pathname.startsWith(`${BASE}/`) && pathname.length > BASE.length + 1;

      if (alreadyOnChildRoute) {
        const tabUrl = pathname.replace(`${BASE}/`, "");
        const routeKey = tabUrl.split(/[?#/]/)[0];

        if (IS_CARRIER && routeKey === "CarrierInviteViewBid") {
          setSelectedTab("menu");
          setShowButtons(false);
        } else if (!restoredTabs || restoredTabs.length === 0) {
          const initialTab = {
            value: "1",
            label: translateLabel(routeKey),
            tabUrl,
            i18nKey: routeKey,
          };

          setTabs([initialTab]);
          setSelectedTab("1");
          setTabIndex(2);
          setShowButtons(false);
          setLastOpenTab("1");
        }
      } else if (!restoredTabs || restoredTabs.length === 0) {
        setSelectedTab("menu");
        navigate(BASE);
        setShowButtons(true);
        setLastOpenTab("");
      }

      setIsFirstTime(false);
    }, 50);

    return () => clearTimeout(timerId);
  }, [isFirstTime, M_StateData, pathname, BASE, IS_CARRIER, navigate, translateLabel]);

  /**
   * Persists the current shell snapshot into Redux after bootstrap completes.
   *
   * Purpose:
   * - keeps tabs, selection and shell visibility recoverable across remounts,
   * - avoids the recursive loop by staying independent from M_StateData updates.
   */
  useEffect(() => {
    if (isFirstTime) return;

    dispatch(M_StateActions.addM_State({ key: "tabs", value: tabs }));
    dispatch(
      M_StateActions.addM_State({
        key: "selectedTab",
        value: selectedTab,
      })
    );
    dispatch(
      M_StateActions.addM_State({
        key: "showButtons",
        value: showButtons,
      })
    );
    dispatch(
      M_StateActions.addM_State({
        key: "tabIndex",
        value: tabIndex,
      })
    );
    dispatch(
      M_StateActions.addM_State({
        key: "lastOpenTab",
        value: lastOpenTab,
      })
    );
  }, [isFirstTime, tabs, selectedTab, showButtons, tabIndex, lastOpenTab, dispatch]);

  //endregion

  //region Tab and menu interaction handlers

  /**
   * Handles tab selection changes from the visible TabList.
   *
   * Behavior:
   * - updates the selected tab snapshot,
   * - routes to the landing menu for pseudo-tab "menu",
   * - routes to the stored tabUrl for normal dynamic tabs.
   *
   * @param {Event} event - Change event.
   * @param {string} newValue - Selected tab value.
   */
  const handleChange = useCallback(
    (event, newValue) => {
      setSelectedTab(newValue);

      dispatch(
        M_StateActions.addM_State({
          key: "selectedTab",
          value: newValue,
        })
      );

      if (newValue === "menu") {
        menusRoute();
      } else {
        const targetTab = tabs.find(
          (tab) => String(tab.value) === String(newValue)
        );

        if (targetTab) {
          tabsRoutes(targetTab.tabUrl);
        }
      }
    },
    [dispatch, tabs, tabsRoutes, menusRoute]
  );

  /**
   * Renames an existing tab by its internal value.
   *
   * Purpose:
   * - allows child pages to adjust the visible caption after loading specific content.
   *
   * @param {string|number} value - Internal tab identifier.
   * @param {string} newLabel - New visible label.
   */
  const renameTab = useCallback((value, newLabel) => {
    setTabs((prev) => {
      let changed = false;

      const next = prev.map((tab) => {
        if (String(tab.value) !== String(value)) {
          return tab;
        }

        if (tab.label === newLabel) {
          return tab;
        }

        changed = true;
        return { ...tab, label: newLabel };
      });

      return changed ? next : prev;
    });
  }, []);

  /**
   * Closes an existing tab and repairs the shell selection afterwards.
   *
   * Behavior:
   * - removes tab-specific persisted Redux state,
   * - removes legacy child table keys when present,
   * - returns to the landing menu when no tabs remain,
   * - otherwise restores the last open tab when possible.
   *
   * @param {string|number} value - Optional tab id. When omitted, closes the selected tab.
   */
  const handleTabClose = useCallback(
    (value) => {
      setIsLoadRef.current?.(true);

      const rawValue =
        value === undefined || value === null ? selectedTab : value;

      const v = typeof rawValue === "string" ? rawValue : `${rawValue}`;

      dispatch(M_StateActions.removeM_State({ key: v }));

      const tempDbTable = M_StateData.find((obj) => obj.key === `"${v}.1"`);
      const tempDbTableChild = M_StateData.find((obj) => obj.key === `"${v}.2"`);

      if (tempDbTable) {
        dispatch(M_StateActions.removeM_State({ key: `"${v}.1"` }));
      }

      if (tempDbTableChild) {
        dispatch(M_StateActions.removeM_State({ key: `"${v}.2"` }));
      }

      setTabs((prevTabs) => {
        const remainingTabs = prevTabs.filter(
          (tab) => String(tab.value) !== String(v)
        );

        setTimeout(() => {
          if (remainingTabs.length === 0) {
            setSelectedTab("menu");
            menusRoute();

            const removableKeys = M_StateData.filter(
              (obj) =>
                obj.key !== "tabs" &&
                obj.key !== "selectedTab" &&
                obj.key !== "showButtons" &&
                obj.key !== "tabIndex" &&
                obj.key !== "lastOpenTab"
            );

            removableKeys.forEach((k) =>
              dispatch(M_StateActions.removeM_State({ key: k.key }))
            );

            toastMessagesRef.current?.(
              "success",
              "",
              translateLabel("MessageTabClosed")
            );
            setIsLoadRef.current?.(false);
            return;
          }

          const lastTab = remainingTabs.find(
            (tab) => String(tab.value) === String(lastOpenTab)
          );

          if (String(selectedTab) === String(v)) {
            if (lastTab) {
              tabsRoutes(lastTab.tabUrl);
              setSelectedTab(String(lastTab.value));
            } else {
              setSelectedTab("menu");
              menusRoute();
            }
          }

          toastMessagesRef.current?.(
            "success",
            "",
            translateLabel("MessageTabClosed")
          );
          setIsLoadRef.current?.(false);
        }, 0);

        return remainingTabs;
      });
    },
    [
      M_StateData,
      dispatch,
      lastOpenTab,
      selectedTab,
      menusRoute,
      tabsRoutes,
      translateLabel,
    ]
  );

  /**
   * Changes the current language and persists the selection in localStorage.
   *
   * @param {string} lng - Selected language code.
   */
  const handleChangeLng = useCallback(
    (lng) => {
      i18n.changeLanguage(lng);
      localStorage.setItem("lng", lng);
    },
    [i18n]
  );

  /**
   * Opens the settings page as a dynamic tab.
   */
  const handleSettings = useCallback(() => {
    goToPage("settings");
  }, [goToPage]);

  /**
   * Opens a menu target route as a dynamic tab.
   *
   * @param {string} route - Menu route.
   * @param {string} label - Menu label used as tab translation key.
   */
  const handleMenuPages = useCallback(
    (route, label) => {
      goToPage(route, label);
    },
    [goToPage]
  );

  //endregion

  //region Menu filtering and context value

  /**
   * Filters the visible menu tree according to the current role collection.
   *
   * Defensive behavior:
   * - always starts from a normalized menu array,
   * - normalizes every submenu collection before filtering,
   * - keeps the raw normalized menu tree visible while roles are not yet available.
   */
  const filteredMenus = useMemo(() => {
    const safeMenus = toMenuArray(menus);

    if (!Array.isArray(roles) || roles.length === 0) {
      return safeMenus;
    }

    return safeMenus
      .filter((menu) => {
        const actionValue = Number(menu?.Action);
        const actionName = !isNaN(actionValue)
          ? ModuleActionEnumReverse[actionValue]
          : null;

        return checkPermissions(menu?.ModuleName, actionName);
      })
      .map((menu) => {
        const safeSubMenus = Array.isArray(menu?.SubMenus) ? menu.SubMenus : [];

        return {
          ...menu,
          SubMenus: safeSubMenus
            .filter((sub) => {
              const actionValue = Number(sub?.Action);
              const actionName = !isNaN(actionValue)
                ? ModuleActionEnumReverse[actionValue]
                : null;

              return checkPermissions(sub?.ModuleName, actionName);
            })
            .map((sub) => {
              const safeSubSubMenus = Array.isArray(sub?.SubMenus)
                ? sub.SubMenus
                : [];

              return {
                ...sub,
                SubMenus: safeSubSubMenus.filter((subsub) => {
                  const actionValue = Number(subsub?.Action);
                  const actionName = !isNaN(actionValue)
                    ? ModuleActionEnumReverse[actionValue]
                    : null;

                  return checkPermissions(subsub?.ModuleName, actionName);
                }),
              };
            }),
        };
      });
  }, [menus, roles, checkPermissions]);

  /**
   * Re-translates open tab labels whenever the active language changes.
   *
   * Purpose:
   * - keeps dynamic tabs aligned with runtime language changes,
   * - avoids redundant state writes when no visible label actually changes.
   */
  useEffect(() => {
    setTabs((prev) => {
      let changed = false;

      const next = prev.map((tab) => {
        if (!tab.i18nKey) {
          return tab;
        }

        const nextLabel = translateLabel(tab.i18nKey);

        if (nextLabel === tab.label) {
          return tab;
        }

        changed = true;
        return { ...tab, label: nextLabel };
      });

      return changed ? next : prev;
    });
  }, [i18n.language, translateLabel]);

  /**
   * Value exposed through NavigateContext.
   *
   * Purpose:
   * - allows child pages to create tabs,
   * - close the current tab,
   * - rename their own tab after loading.
   */
  const contextCallback = useMemo(
    () => ({ newTab: goToPage, closeTab: handleTabClose, renameTab }),
    [goToPage, handleTabClose, renameTab]
  );

  //endregion

  //region Render

  return (
    <NavigateContext.Provider value={contextCallback}>
      <Grid
        container
        className="divTabs"
        style={{
          "--tabs-bg": ctx.secondaryColor || "var(--color-red-dark)",
          "--tabs-fg": "var(--color-white)",
          "--tabs-selected-fg": ctx.secondaryColor || "var(--color-black)",
        }}
      >
        <Grid
          size={{ xs: 0.2, sm: 0.7, md: 1.5, lg: 1.5 }}
          className="wrapperLogoMenu"
        >
          <MButton
            none
            noHover
            backgroundColor={ctx.secondaryColor || "var(--color-red-dark)"}
            tooltip={t("HomePage")}
            onClick={handleLogoClick}
            icon={
              <picture className="topBarLogoPicture">
                {/* Use the compact square logo on narrow screens to preserve tab space. */}
                <source
                  media="(max-width: 1200px)"
                  srcSet="./images/LogoShipperForm.png"
                />
                <img
                  className="logoImage"
                  src="./images/ShipperFormSmallWhite.png"
                  alt="home"
                />
              </picture>
            }
          />

          <Tooltip title="Menu">
            <IconButton
              aria-haspopup="true"
              size="large"
              disableRipple={true}
              onClick={() => setShowMenu((s) => !s)}
              className="menuIconStyle"
            >
              <i className="buttonFavorites pi pi-bars" />
            </IconButton>
          </Tooltip>
        </Grid>

        <Grid size={{ xs: 4.6, sm: 7.8, md: 7.0, lg: 8.5 }}>
          <TabContext value={uiTabValue}>
            <TabList
              scrollButtons={tabs?.length !== 0}
              allowScrollButtonsMobile
              TabIndicatorProps={{ style: { display: "none" } }}
              className="tabList"
              onChange={handleChange}
              aria-label="Dynamic Tabs"
              variant="scrollable"
              value={uiTabValue}
            >
              <Tab
                value="menu"
                label="menu"
                disableRipple={true}
                className="selectedTabDefault"
              />

              {tabs.map((tab) => {
                const rawLabel = tab.label || "";

                return (
                  <Tab
                    key={tab.value}
                    disableRipple={true}
                    value={tab.value}
                    onMouseDown={(event) => {
                      if (event.button === 1) {
                        document.body.onmousedown = (e) => {
                          if (e.button === 1) return false;
                          return true;
                        };

                        handleTabClose(tab.value);
                      }
                    }}
                    label={<TabLabel text={rawLabel} />}
                    icon={
                      <Close
                        className="tabCloseIcon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTabClose(tab.value);
                        }}
                      />
                    }
                    iconPosition="end"
                  />
                );
              })}
            </TabList>
          </TabContext>
        </Grid>

        <Grid size={{ xs: 0.2, sm: 0.5, md: 0.5, lg: 2.0 }}>
          <div className="divAccount" style={{ position: "relative" }}>
            <span className="accountInfo">
              {IS_CARRIER ? (
                <>
                  {carrierAccountName || ""}
                  <span className="accountInfoSeparator"> | </span>
                  <span className="shipperInfo">{carrierName || ""}</span>
                </>
              ) : (
                <>
                  {defaultAccountName || ""}
                  <span className="accountInfoSeparator"> | </span>
                  <span className="shipperInfo">
                    {defaultShipperName || ""}
                  </span>
                </>
              )}
            </span>
          </div>
        </Grid>
      </Grid>

      <MLateralMenu
        handleChangeLng={handleChangeLng}
        handleLogout={handleLogout}
        handleSettings={handleSettings}
        handleMenuPages={handleMenuPages}
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        menus={filteredMenus}
        setMenus={setMenus}
        bodyPage={
          <div className="homeContentShell">
            {showButtons && (
              <div className="homeLandingLayer">
                <div className="containerCentral">
                  <div className="logoBox">
                    <img
                      src="./images/LogoShipperForm.png"
                      alt="ShipperformLogo"
                    />
                  </div>
                  <div className="footerBox">
                    <img src="./images/nexus.png" alt="NexusLogo" />
                  </div>
                </div>
              </div>
            )}

            <div className="homeRoutesLayer">
              <div className="baseLine">
                <ScrollToTop
                  smooth
                  component={
                    <ArrowUpwardIcon
                      fontSize="large"
                      style={{ color: "var(--color-white)" }}
                    />
                  }
                  style={{
                    backgroundColor: `${ctx.primaryColor
                      ? ctx.primaryColor
                      : "var(--color-black)"
                      } !important`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "45px",
                    height: "45px",
                  }}
                />

                <Suspense fallback={<LoadingBar />}>
                  <Routes>
                    <Route path="/changeLog" element={<ChangeLogPage />} />

                    <Route
                      path="/settings"
                      element={
                        <SettingsPage
                          roles={roles}
                          checkPermissions={checkPermissions}
                        />
                      }
                    />

                    <Route
                      path="/UserConfig"
                      element={
                        <UserConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/RoleConfig"
                      element={
                        <RoleConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/accounts/*"
                      element={
                        <AccountPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/accounts/accountConfigCrud"
                      element={
                        <AccountConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/contacts/*"
                      element={
                        <ContactPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/contacts/contactConfigCrud"
                      element={
                        <ContactConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/shippers/*"
                      element={
                        <ShipperConfigPage
                          roles={roles}
                          onCreateTab={goToPage}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/shipper-markets/*"
                      element={
                        <ShipperMarketManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/shipper-carriers/*"
                      element={
                        <ShipperCarrierManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/shippers/shipperConfigCrud"
                      element={
                        <ShipperConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/carriers"
                      element={
                        <CarrierConfigPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/carrier-markets"
                      element={
                        <CarrierMarketManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/carrier-typesOfService"
                      element={
                        <CarrierTypeOfServiceManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/carrier-logins"
                      element={
                        <CarrierLoginsConfigPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/carriers/carrierConfigCrud"
                      element={
                        <CarrierConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/carriers/carrierLoginsConfigCrud"
                      element={
                        <CarrierLoginsConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/markets/*"
                      element={
                        <MarketPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/markets/marketConfigCrud"
                      element={
                        <MarketConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/locations/*"
                      element={
                        <LocationPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/locations/locationConfigCrud"
                      element={
                        <LocationConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/countries/*"
                      element={
                        <CountryPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/countries/CountryConfigCrud"
                      element={
                        <CountryConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/countries/ContinentConfigCrud"
                      element={
                        <ContinentConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/currencies/*"
                      element={
                        <CurrencyPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/currencies/currencyConfigCrud"
                      element={
                        <CurrencyConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/transport-types/*"
                      element={
                        <TransportTypePage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/transport-types/transportTypeConfigCrud"
                      element={
                        <TransportTypeConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/lanes/*"
                      element={
                        <LanePage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/lanes/laneConfigCrud"
                      element={
                        <LaneConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/routes/*"
                      element={
                        <RoutePage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/routes/routeConfigCrud"
                      element={
                        <RouteConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/routes/routeLaneConfigCrud"
                      element={
                        <RouteLaneConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/instant-quotes/*"
                      element={
                        <InstantQuotesSearchPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/instant-quotes/instant-quotesConfigCrud"
                      element={
                        <InstantQuotesSearchConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/instant-quotes/instantQuotesConfigCrud"
                      element={
                        <InstantQuotesConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tenders/spot-tenders/*"
                      element={
                        IS_CARRIER ? (
                          <SpotTenderCarrierInvitePage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        ) : (
                          <SpotTenderPage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        )
                      }
                    />

                    <Route
                      path="/tenders/non-spot-tenders/*"
                      element={
                        IS_CARRIER ? (
                          <NonSpotTenderCarrierInvitePage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        ) : (
                          <NonSpotTenderPage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        )
                      }
                    />

                    <Route
                      path="/create-tenders/*"
                      element={
                        <CreateTenderPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tenders/tenderOpenQuotation"
                      element={
                        <TenderOpenPage
                          workflowMode="quotation"
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tenders/tenderOpenAnalysis"
                      element={
                        <TenderOpenPage
                          workflowMode="analysis"
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tenders/non-spot-tenders/tenderPhaseConfig"
                      element={
                        <TenderPhaseConfig
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tenders/tenderFinished"
                      element={
                        <TenderFinishedPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/addresses/*"
                      element={
                        <AddressPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/addresses/AddressConfigCrud"
                      element={
                        <AddressConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/ApiKeyConfig"
                      element={
                        <ApiKeyConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tenders/TenderConfigCrud"
                      element={
                        <TenderConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                          isCarrierRealm={false}
                        />
                      }
                    />

                    <Route
                      path="/tenders/CarrierSubmitPage"
                      element={
                        <CarrierSubmitPage
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/CarrierInviteViewBid"
                      element={
                        <CarrierInviteViewBidPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                          onForceLogout={handleLogout}
                        />
                      }
                    />

                    <Route
                      path="/tracking/*"
                      element={
                        <TrackingPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tracking/trackingConfigCrud"
                      element={
                        <TrackingConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                  </Routes>
                </Suspense>
              </div>
            </div>
          </div>
        }
      />
    </NavigateContext.Provider>
  );

  //endregion
}

TabsHomePage.propTypes = {
  roles: PropTypes.array,
};

//endregion